// Table
import Table from '../ui/table/Table'

// UI
import { Anchor } from '@mantine/core'
import Badge from '@/components/ui/Badge'

// Next
import { Link } from 'react-router-dom'

// API
import { useApiSWRReference } from '@/hooks/useApiSWR'

// Dayjs
import dayjs from 'dayjs'

// Statuses
import {
    getEstimateStatus,
    getEstimateFulfillmentStatus,
    getInvoiceStatus,
    estimate_statuses,
} from '@/lib/retailerStatuses'

// Lodash OrderBy
import orderBy from 'lodash/orderBy'

// Components
import ProfileAvatar from '@/components/ui/ProfileAvatar'

// Cells
const NameCell = (item) => (
    <Link
        to={`/retailer_estimates/${item.id}`}
        className="table-cell p-5 hover:bg-gray-100"
    >
        <div className="flex gap-6 items-center">
            <div className="flex flex-col items-start">
                <Anchor component="span">{item.number}</Anchor>

                <span className="text-gray-500 text-sm">
                    {dayjs(item.date).format('ll')}
                </span>
            </div>
        </div>
    </Link>
)

const RetailerCell = (item) => (
    <Link
        to={`/retailers/${item.retailer.id}`}
        className="table-cell p-5 hover:bg-gray-100 align-middle"
    >
        <div className="flex gap-6 items-center">
            <div className="flex flex-col items-start">
                <Anchor component="span">{item.retailer.name}</Anchor>
            </div>
        </div>
    </Link>
)

const StatusCell = (item) => {
    const estimate_status = getEstimateStatus(item.status)

    return (
        <div className="table-cell p-5 align-middle text-sm">
            <Badge color={estimate_status.badgeColor}>
                {estimate_status.label}
            </Badge>
        </div>
    )
}

const TotalCell = (item) => (
    <div className="table-cell p-5 align-middle">
        <span className="text-gray-500 text-sm uppercase">
            {new Intl.NumberFormat('fr-FR', {
                style: 'currency',
                currency: 'EUR',
            }).format(item.amounts.total)}
        </span>
    </div>
)

const FulfillmentStatusCell = (item) => {
    const fulfillment_status = getEstimateFulfillmentStatus(
        item.fulfillment_status
    )

    return (
        <div className="table-cell p-5 text-sm align-middle">
            <Badge color={fulfillment_status.badgeColor}>
                {fulfillment_status.label}
            </Badge>
        </div>
    )
}

const FinancialStatusCell = (item) => {
    return (
        <div className="table-cell p-5 text-sm align-middle">
            <div className="flex flex-col">
                {item.invoices.map((invoice) => {
                    const invoice_status = getInvoiceStatus(invoice.status)

                    return (
                        <div key={invoice.id}>
                            <Badge
                                key={invoice.id}
                                color={invoice_status.badgeColor}
                            >
                                {invoice_status.label}
                            </Badge>
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

const CreatedByCell = (item) => (
    <div className="table-cell p-5 align-middle">
        {item.created_by_profile ? (
            <ProfileAvatar profile={item.created_by_profile} />
        ) : (
            <span className="text-gray-400 text-sm">Espace revendeur</span>
        )}
    </div>
)

export default function RetailerEstimateTable() {
    const availableColumns = [
        {
            name: 'Nom',
            key: 'id',
            display: 'mandatory',
            sortable: true,
            component: NameCell,
        },
        {
            name: 'Revendeur',
            key: 'retailer',
            display: 'optional_default',
            sortable: false,
            component: RetailerCell,
        },
        {
            name: 'Statut',
            key: 'status',
            display: 'optional_default',
            sortable: false,
            component: StatusCell,
        },
        {
            name: 'Total TTC',
            key: 'total',
            display: 'optional_default',
            sortable: false,
            component: TotalCell,
        },
        {
            name: 'Statut logistique',
            key: 'fulfillment_status',
            display: 'optional_default',
            sortable: false,
            component: FulfillmentStatusCell,
        },
        {
            name: 'Factures',
            key: 'financial_status',
            display: 'optional_default',
            sortable: false,
            component: FinancialStatusCell,
        },
        {
            name: 'Créé par',
            key: 'created_by_profile',
            display: 'optional',
            sortable: false,
            component: CreatedByCell,
        },
    ]

    // Load filters options
    const { data: retailers } = useApiSWRReference('/retailers/list')

    const statusOptions = orderBy(
        Object.keys(estimate_statuses).map((key) => ({
            label: getEstimateStatus(key).label,
            value: key,
        })),
        'label'
    )

    const availableFilters = [
        {
            name: 'Revendeur',
            key: 'retailer_id',
            type: 'multiselect',
            placeholder: 'Filter par revendeur...',
            options: retailers?.map((retailer) => ({
                label: retailer.name,
                value: retailer.id.toString(),
            })),
        },
        {
            name: 'Statut',
            key: 'status',
            type: 'multiselect',
            placeholder: 'Filter par statut...',
            options: statusOptions,
        },
    ]

    return (
        <Table
            engineApiEndpoint="retailer-estimates"
            availableColumns={availableColumns}
            availableFilters={availableFilters}
            searchable={false}
            defaultSortColumn="id"
            defaultSortDirection="desc"
        />
    )
}
